export type PwaEnvironmentKind =
  'installed' | 'android-chrome' | 'ios' | 'compatible' | 'incompatible'

export interface PwaEnvironment {
  kind: PwaEnvironmentKind
  installable: boolean
}

export interface PwaEnvironmentInput {
  userAgent: string
  standalone: boolean
  iosStandalone: boolean
  serviceWorkerSupported: boolean
  indexedDbSupported: boolean
}

interface InstallChoice {
  outcome: 'accepted' | 'dismissed'
  platform: string
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<InstallChoice>
}

type Listener = () => void

const listeners = new Set<Listener>()
let deferredPrompt: BeforeInstallPromptEvent | undefined
let installedByEvent = false
let initialized = false
let snapshot: PwaEnvironment = {
  kind: 'incompatible',
  installable: false,
}

function browserEnvironmentInput(): PwaEnvironmentInput {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean }
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    document.referrer.startsWith('android-app://')
  return {
    userAgent: navigator.userAgent,
    standalone,
    iosStandalone: navigatorWithStandalone.standalone === true,
    serviceWorkerSupported: 'serviceWorker' in navigator,
    indexedDbSupported: 'indexedDB' in window,
  }
}

export function detectPwaEnvironment(input: PwaEnvironmentInput): PwaEnvironmentKind {
  if (input.standalone || input.iosStandalone) return 'installed'
  if (!input.serviceWorkerSupported || !input.indexedDbSupported) return 'incompatible'

  const userAgent = input.userAgent
  const ios =
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (/Macintosh/i.test(userAgent) && /Mobile/i.test(userAgent))
  if (ios) return 'ios'

  const androidChrome =
    /Android/i.test(userAgent) &&
    /Chrome\/\d+/i.test(userAgent) &&
    !/(?:EdgA|OPR|SamsungBrowser)\//i.test(userAgent)
  return androidChrome ? 'android-chrome' : 'compatible'
}

function publish() {
  snapshot = {
    kind: installedByEvent ? 'installed' : detectPwaEnvironment(browserEnvironmentInput()),
    installable: Boolean(deferredPrompt) && !installedByEvent,
  }
  listeners.forEach((listener) => listener())
}

export function initializePwaInstallPromptCapture() {
  if (initialized || typeof window === 'undefined') return
  initialized = true
  publish()
  window.addEventListener('beforeinstallprompt', (rawEvent) => {
    const event = rawEvent as BeforeInstallPromptEvent
    event.preventDefault()
    deferredPrompt = event
    publish()
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = undefined
    installedByEvent = true
    publish()
  })
}

export function subscribePwaInstallState(listener: Listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getPwaInstallState() {
  return snapshot
}

export async function requestPwaInstallation(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const prompt = deferredPrompt
  if (!prompt) return 'unavailable'
  await prompt.prompt()
  const choice = await prompt.userChoice
  deferredPrompt = undefined
  if (choice.outcome === 'accepted') installedByEvent = true
  publish()
  return choice.outcome
}
